import React from "react";
import { startCase } from "lodash";

import { makeStyles } from "@material-ui/core";
import { formatDistanceToNow } from "date-fns";
import { Button } from "semantic-ui-react";
import { formatDate } from "utils/dates";

import { useSelection } from "hooks";
import { useFeatures } from "components/FeatureFlag";
import Table from "components/Table";

import { useResourcesSharedWithMe, useUpdateSharedResources } from "./hooks";
import { SharedResourceStatus } from "..";

const useStyles = makeStyles({
  table: {
    border: "1px solid #DADADA",
    borderRadius: "4px",
  },
  headerCell: {
    backgroundColor: "#F9F9F9",
    fontSize: "14px",
    fontWeight: "bold",
    padding: "0.92857143em 0.78571429em",
  },
  bodyCell: {
    fontSize: "14px",
    padding: "0.78571429em 0.78571429em",
  },
});

function ResourcesSharedWithMe() {
  const classes = useStyles();

  const {
    sharedResources,
    initialLoading,
    isReloading,
    totalCount,
    pageControl,
    updatePageControl,
    reloadData,
  } = useResourcesSharedWithMe();

  const selection = useSelection(sharedResources);

  const { updateSharedResources, isUpdating } = useUpdateSharedResources(() => {
    reloadData();
    selection.clearSelection();
  });

  const sharedResourcesFlag = useFeatures("shared-resources");

  const tableColumns = [
    "resourceName",
    "version",
    "sharedBy",
    "sharedOn",
    "status",
  ].map((id, index) => {
    return {
      id: id,
      label:
        sharedResourcesFlag?.settings?.sharedWithMeTab?.table?.columns[index] ||
        startCase(id),
    };
  });

  return (
    <React.Fragment>
      <Table
        className={classes.table}
        data-test="resources-shared-with-me-table"
      >
        <Table.Content>
          <Table.Header
            columns={tableColumns}
            className={classes.headerCell}
            onSelectAll={selection.onSelectAll}
            loading={initialLoading}
            isReloading={isReloading || isUpdating}
            isEmpty={sharedResources?.length === 0 || !sharedResources}
            selectedCount={selection.selected.length}
            rowCount={sharedResources?.length}
          />
          <Table.Body
            isLoading={initialLoading}
            isReloading={isReloading || isUpdating}
            columns={tableColumns}
            data={sharedResources?.map((sharedResource) => {
              return {
                ...sharedResource,
                sharedOn: (
                  <span
                    title={formatDate("simpleDateTime")(
                      sharedResource.sharedOn
                    )}
                  >
                    {formatDistanceToNow(sharedResource.sharedOn, {
                      addSuffix: true,
                      includeSeconds: true,
                    })}
                  </span>
                ),
                status: <SharedResourceStatus status={sharedResource.status} />,
              };
            })}
            className={classes.bodyCell}
            emptyDataMessage={
              sharedResourcesFlag?.settings?.sharedWithMeTab?.table
                ?.emptyMessage || "You haven't shared any resources yet"
            }
            onRowClick={selection.onSelectRow}
            checkSelected={selection.checkSelected}
          />
        </Table.Content>
      </Table>
      <Table.PaginationControl
        isLoading={initialLoading || isReloading || isUpdating}
        totalCount={totalCount}
        page={pageControl.page}
        pageSize={pageControl.pageSize}
        onPageChange={updatePageControl}
        onPageSizeChange={updatePageControl}
      />

      {sharedResources?.length > 0 && (
        <Button
          primary
          content="Install"
          disabled={selection.selected.length === 0 || isUpdating}
          onClick={() => {
            updateSharedResources(selection.selected, "INSTALLED");
          }}
        />
      )}
    </React.Fragment>
  );
}

export default ResourcesSharedWithMe;
